import React from "react";
import {
  Box,
  Grid2,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import { ALERTS_EMPTY_VALUE } from "./alertsDashboardFormat";

const CELL_FONT_PX = 11;
const cellSx = {
  textAlign: "center" as const,
  fontSize: CELL_FONT_PX,
  padding: "4px 8px",
};

export interface AlertsInfoItem {
  label: string;
  value: React.ReactNode;
}

/** Label / value grid for detail headers. Empty values render as "—". */
export const AlertsInfoGrid = ({ items }: { items: AlertsInfoItem[] }) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;
  return (
    <Grid2 container spacing={1.5}>
      {items.map((item) => (
        <Grid2 key={item.label} size={{ xs: 6, sm: 4, md: 3 }}>
          <CustomTypography
            variant="body4"
            sx={{
              color: customColors.mutedSlateText,
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.02em",
            }}
          >
            {item.label}
          </CustomTypography>
          <Box
            sx={{
              mt: 0.25,
              fontSize: "0.8rem",
              color: customColors.navyText,
              wordBreak: "break-word",
            }}
          >
            {item.value === null ||
            item.value === undefined ||
            item.value === ""
              ? ALERTS_EMPTY_VALUE
              : item.value}
          </Box>
        </Grid2>
      ))}
    </Grid2>
  );
};

export interface AlertsSimpleColumn<T> {
  label: string;
  render: (row: T) => React.ReactNode;
}

/** Compact 11px table for detail sub-lists (no paging; the server sends all rows). */
export function AlertsSimpleTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  expandedKey,
  renderExpanded,
}: {
  columns: AlertsSimpleColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  expandedKey?: string | null;
  renderExpanded?: (row: T) => React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small">
          <TableHead sx={{ position: "relative", zIndex: 30 }}>
            <TableRow>
              {columns.map((column) => (
                <AppTableHeaderCell key={column.label} sx={cellSx}>
                  {column.label}
                </AppTableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const key = rowKey(row, index);
              const expanded = Boolean(renderExpanded) && expandedKey === key;
              return (
                <React.Fragment key={key}>
                  <TableRow
                    hover
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      backgroundColor:
                        index % 2 === 0
                          ? theme.palette.action.hover
                          : theme.palette.background.paper,
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                  >
                    {columns.map((column) => (
                      <AppTableRowCell key={column.label} sx={cellSx}>
                        {column.render(row)}
                      </AppTableRowCell>
                    ))}
                  </TableRow>
                  {expanded && renderExpanded ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        sx={{
                          p: 1.5,
                          bgcolor: theme.palette.customColors.veryLightGray,
                        }}
                      >
                        {renderExpanded(row)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/** Button-style link (stops row clicks). */
export const AlertsInlineLink = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <Link
    component="button"
    type="button"
    underline="hover"
    onClick={(event: React.MouseEvent) => {
      event.stopPropagation();
      onClick();
    }}
    sx={{ fontSize: "inherit", fontWeight: 600 }}
  >
    {label}
  </Link>
);

/** Toggle row of AppButtons (selected → primary). */
export function AlertsToggleRow<T extends string>({
  title,
  options,
  value,
  onChange,
}: {
  title?: string;
  options: Array<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}) {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
    >
      {title ? (
        <CustomTypography
          variant="heading7"
          sx={{ color: colors.title, mr: 0.5 }}
        >
          {title}
        </CustomTypography>
      ) : null}
      {options.map((option) => (
        <AppButton
          key={option.id}
          label={option.label}
          variant={value === option.id ? "primary" : "transparentPrimary"}
          onClick={() => onChange(option.id)}
          customStyles={{ minHeight: 32, px: 1.5 }}
          fontSize="0.75rem"
        />
      ))}
    </Box>
  );
}
